import * as Sentry from '@sentry/react';

import { User } from 'src/domain/User';
import { PROVIDERS_ROLES } from 'src/utils/constants';
import env, { mode } from 'src/utils/env';

const shouldLogToSentry = env.LOGTO_SENTRY;
const sentryDSN = env.SENTRY_DSN;
const currentCommit = env.CURRENT_COMMIT;

if (shouldLogToSentry && !sentryDSN) {
  throw new Error('Missing SENTRY_DSN value on the enviroment variables');
}

if (shouldLogToSentry) {
  Sentry.init({
    dsn: sentryDSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        workerUrl: '/assets/sentry-worker.js',
        unmask: ['.sentry-unmask, [data-sentry-unmask]'],
      }),
    ],
    sampleRate: 1,
    release: currentCommit,
    environment: env.SENTRY_ENVIRONMENT,
    tracesSampleRate: 0.1,
    denyUrls: ['localhost'],
    // Our Sentry plan includes 50 replays/month, and recording a share of
    // *every* session spent that within two days of each billing cycle. Record
    // nothing for a session that goes fine, and a tenth of the ones that hit an
    // error. Keep these two rates the same across every app.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
  });
}

export class Logger {
  static configureScope(user: User) {
    if (shouldLogToSentry) {
      Sentry.setUser({
        username: user.username,
        id: user.providerId!.toString(),
        role: PROVIDERS_ROLES[user.roleId!],
        unitsPreference: user.unitsPreference,
      });
    }
  }

  static captureMessage(message: string) {
    if (shouldLogToSentry) {
      Sentry.captureMessage(message);
    } else {
      // eslint-disable-next-line no-console
      console.info(message);
    }
  }

  /**
   * `context` reaches Sentry's `captureException` untouched, so a caller can set
   * a `fingerprint` and decide how its events group. That matters for errors
   * raised from shared helpers: without one, Sentry groups on the stack, and
   * every caller of the helper collapses into a single issue.
   */
  static captureException(exception: any, context?: Parameters<typeof Sentry.captureException>[1]) {
    if (shouldLogToSentry) {
      Sentry.captureException(exception, context);
    } else {
      // eslint-disable-next-line no-console
      console.error(exception);
    }
  }

  static debug(...messages: unknown[]) {
    if (mode === 'dev') {
      // eslint-disable-next-line no-console
      console.log(...messages);
    }
  }
}
